import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Microphone, Camera, ArrowRight, ArrowLeft, Check, Spinner, Trash,
  Sparkle, Stop, Pause, Play, Lightning, Image as ImageIcon,
  PencilSimple, FloppyDisk, X as XIcon, MagicWand as CleanWand,
  House,
} from "@phosphor-icons/react";
import { api, apiError, notifyError } from "@/lib/api";
import { compressPhoto } from "@/lib/compressPhoto";
import { enqueuePhoto, dequeuePhoto, listPending, incrementRetry, pruneOld } from "@/lib/photoQueue";
import { saveRecordingChunk, getStoredRecording, clearStoredRecording } from "@/lib/recordingBackup";
import InAppCameraCapture from "@/components/InAppCameraCapture";
import { nearbySummary } from "@/lib/nearbyPois";
import CheckListPromemoria from "./CheckListPromemoria";
import { fileUrl } from "./shared";

// iter v28 r427 — Code review point 10: helper per loggare silenziosamente
// in dev mode gli errori dei catch "best-effort" (cleanup mic, audio context
// close, geocoder enrichment opzionale). In produzione resta silent come
// prima — l'utente non vede nulla. In dev il developer ha visibilità.
function devWarn(label, err) {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(`[RecordingPhase:${label}]`, err);
  }
}

// iter v28 r349 — Sopralluogo a fasi sequenziali.
//
// User feedback (audio fuori sede):
//   1. «avevo schiacciato Termina e mi aveva cancellato tutto» — il trash
//      icon in alto-destra senza label confondeva: l'agente lo associava al
//      bottone "Termina" intendendo "ho finito" → invece cancellava la
//      sessione. RIMOSSO dal header, spostato in fondo come azione esplicita.
//   2. «la guida di cosa dire va fatta meglio» — checklist con auto-spunta
//      via keywords nel transcript. L'agente vede in tempo reale cosa manca.
//   3. «uno fa le foto dopo che ha detto le cose» — flusso ora SEQUENZIALE:
//      Step 1 Racconta → Step 2 Fotografa → Step 3 Genera. Niente più mic +
//      camera mescolati: una cosa alla volta, niente "casino".
//   4. «posizione dei bottoni non chiara» — stepper in alto + CTA primario
//      sempre in fondo, una sola azione al centro per ogni step.

function PhaseStepper({ phase, hasTranscript, photoCount, hasPlan }) {
  // iter v32 r48 · User M1462 — aggiunto step "plan" tra "photo" e "finalize".
  // Stepper a 4 punti. Lo step "plan" è opzionale: si considera "done"
  // anche se l'utente l'ha saltato (hasPlan può rimanere false).
  const steps = [
    { key: "talk", n: 1, label: "Racconta" },
    { key: "photo", n: 2, label: "Fotografa" },
    { key: "plan", n: 3, label: "Pianta", optional: true },
    { key: "finalize", n: 4, label: "Genera" },
  ];
  return (
    <div className="flex items-center gap-1 mb-4" data-testid="phase-stepper">
      {steps.map((s, i) => {
        const isActive =
          phase === s.key ||
          (s.key === "finalize" && phase === "plan" && (photoCount > 0 || hasTranscript));
        const isDone =
          (s.key === "talk" && (phase === "photo" || phase === "plan")) ||
          (s.key === "photo" && photoCount > 0 && phase !== "talk" && phase !== "photo") ||
          (s.key === "plan" && phase !== "talk" && phase !== "photo" && phase !== "plan");
        return (
          <React.Fragment key={s.key}>
            <div
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full transition-all text-[11px] font-semibold ${
                isActive
                  ? "bg-[#1C1C1A] text-white"
                  : isDone
                    ? "bg-[#7C8B6E]/15 text-[#7C8B6E]"
                    : "bg-[#F1F4ED] text-[#9A968B]"
              }`}
              data-testid={`step-${s.key}`}
              data-active={isActive ? "true" : "false"}
            >
              <span
                className={`w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] ${
                  isActive ? "bg-white text-[#1C1C1A]" : isDone ? "bg-[#7C8B6E] text-white" : "bg-white text-[#9A968B]"
                }`}
              >
                {isDone ? <Check size={9} weight="bold" /> : s.n}
              </span>
              <span>{s.label}{s.optional && !isActive && !isDone ? "" : ""}</span>
            </div>
            {i < steps.length - 1 && (
              <span className={`h-px w-2 sm:w-4 ${isDone ? "bg-[#7C8B6E]" : "bg-[#E5E2D8]"}`} aria-hidden />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}


function RecordingPhase({ session, onRefresh, onFinalize, onDiscard }) {
  // iter v28 r349 — Nuovo state machine semplice:
  //   "talk"  → solo microfono + checklist
  //   "photo" → solo fotocamera + galleria
  // Il "finalize" è gestito dal bottone in fondo (rimane sempre raggiungibile
  // da "photo", non un terzo step indipendente).
  const [phase, setPhase] = useState("talk");

  const [rec, setRec] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [uploading, setUploading] = useState(false);
  // iter v28 r380 — Progress upload multi-foto (User M1290 «controlla se
  // uno carica venti foto che non ci sono problemi»). Stato: {done, total}
  // viene mostrato come "Carico foto 3/20…" sul bottone della camera.
  const [uploadProgress, setUploadProgress] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [goingToPhoto, setGoingToPhoto] = useState(false);
  const [recTime, setRecTime] = useState(0);
  // iter v29 r243 — User M1399 «non posso modificare le parole scritte
  // a mano». Edit inline della trascrizione + override locale che evita
  // di dover refetchare la sessione subito dopo il salvataggio.
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [transcriptOverride, setTranscriptOverride] = useState(null);
  // iter v29 r246 — User M1399 (3a iter): "Sistema testo" LLM-cleanup
  // chiama /transcript-clean per ripulire nomi vie / correzioni vocali.
  const [cleaningTranscript, setCleaningTranscript] = useState(false);
  const photoInputRef = useRef(null);
  const recTimerRef = useRef(null);

  const photos = (session.photos || []).slice().sort(
    (a, b) => (new Date(a.created_at) - new Date(b.created_at)),
  );
  const totalPhotos = photos.length;
  // iter v32 r48 · User M1462 — La planimetria è opzionale ma se l'agente
  // l'ha già caricata (sia esplicitamente dallo step "plan", sia tramite
  // auto-classify durante "Fotografa"), saltiamo lo step dedicato.
  const planPhotos = photos.filter((p) => p.photo_kind === "floor_plan");
  const hasPlan = planPhotos.length > 0;
  const rawAggregatedTranscript = Object.values(session.rooms || {})
    .map((r) => (r.transcript || "").trim())
    .filter(Boolean)
    .join("\n");
  // iter v29 r248 — Se il raw è cresciuto oltre l'override (= sono arrivate
  // nuove chunk vocali dopo l'ultima pulizia), preferisci il raw così la
  // nuova trascrizione è subito visibile. Override ha senso solo finché il
  // raw è ≤ del cleaned (= clean appena fatto, nessuna chunk nuova).
  const overrideStillValid =
    transcriptOverride !== null &&
    (rawAggregatedTranscript || "").length <= transcriptOverride.length + 2;
  const aggregatedTranscript = overrideStillValid
    ? transcriptOverride
    : rawAggregatedTranscript;
  const totalTranscriptWords = aggregatedTranscript
    .split(/\s+/).filter(Boolean).length;
  const hasTranscript = totalTranscriptWords > 0;

  // iter v33 r24 · User M1500 — Recovery registrazione persa.
  // Al mount, controllo se c'e' un backup IndexedDB < 24h di una
  // registrazione mai terminata (app messa in background). Se si',
  // offro all'utente di trascriverla adesso invece di perderla.
  const [recoveryOffer, setRecoveryOffer] = useState(null); // { blob, durationS, sessionId }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await getStoredRecording();
      if (cancelled || !stored) return;
      // Ignora se il backup e' della stessa sessione live (ci pensa il
      // flow normale). Interessa solo se e' di una sessione precedente.
      if (stored.sessionId === session?.id) return;
      setRecoveryOffer(stored);
    })();
    return () => { cancelled = true; };
  }, [session?.id]);

  const recoverLostRecording = async () => {
    if (!recoveryOffer) return;
    setRecoveryOffer(null);
    try {
      await uploadVoice(recoveryOffer.blob, 0.5);
    } catch (_e) { /* toast già fatto */ }
    await clearStoredRecording();
  };

  const dismissRecoveryOffer = async () => {
    setRecoveryOffer(null);
    await clearStoredRecording();
  };

  // ── Audio recording (MediaRecorder) ─────────────────────────────
  const [audioLevel, setAudioLevel] = useState(0);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const micStreamRef = useRef(null);
  // iter v32 r49j — User M1457: «non funziona piu il sopralluogo, parlo,
  // concludi e torna a inizia a parlare». Causa: su iOS native (Capacitor)
  // a volte MediaRecorder cattura uno stream silenzioso (permesso mic
  // revocato, app multitasking, ecc.) → Whisper risponde silent → la UI
  // resettava SENZA dire nulla all'utente. Ora teniamo il picco massimo
  // di audio level durante la registrazione: se al termine è < 0.02 (=
  // virtualmente silenzio) avvisiamo esplicitamente prima ancora di
  // chiamare il backend.
  const maxAudioLevelRef = useRef(0);

  const releaseMicrophoneImmediate = () => {
    try {
      const s = micStreamRef.current;
      if (s) {
        s.getTracks().forEach((t) => { try { t.stop(); } catch (e) { devWarn("track-stop", e); } });
        micStreamRef.current = null;
      }
    } catch (e) { devWarn("release-mic", e); }
    try { if (rafRef.current) cancelAnimationFrame(rafRef.current); } catch (e) { devWarn("raf-cancel", e); }
    try {
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch((e) => devWarn("audioctx-close-async", e));
        audioCtxRef.current = null;
      }
    } catch (e) { devWarn("audioctx-close", e); }
    setAudioLevel(0);
  };

  const startRecording = async () => {
    try {
      // iter v32 r49j — Reset peak detector ad ogni nuova registrazione
      maxAudioLevelRef.current = 0;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mime = mimeCandidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      // iter v33 r24 · User M1500 — Salvataggio periodico chunk in IndexedDB.
      // Se l'utente mette l'app in background su iOS senza aver premuto
      // "Concludi", i chunks sono al sicuro e alla prossima apertura di
      // RecordingPhase mostriamo un banner "Recupera registrazione".
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
          // Salva un backup accumulato (throttled a 5s)
          try {
            const partial = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
            const secondsSoFar = Math.max(1, Math.round(chunks.length * 5));
            saveRecordingChunk(session?.id || "no-session", partial, secondsSoFar);
          } catch (_e) { /* silent */ }
        }
      };

      // Live audio analyser
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          audioCtxRef.current = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          const bufferLen = analyser.frequencyBinCount;
          const data = new Uint8Array(bufferLen);
          const tick = () => {
            analyser.getByteFrequencyData(data);
            let sum = 0;
            const half = Math.floor(bufferLen / 2);
            for (let i = 0; i < half; i++) sum += data[i];
            const avg = sum / half / 255;
            setAudioLevel(avg);
            // iter v32 r49j — track picco per warning silenzio
            if (avg > maxAudioLevelRef.current) {
              maxAudioLevelRef.current = avg;
            }
            rafRef.current = requestAnimationFrame(tick);
          };
          rafRef.current = requestAnimationFrame(tick);
        }
      } catch (e) { devWarn("analyser-init", e); }

      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
        clearInterval(recTimerRef.current);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (audioCtxRef.current) {
          try { await audioCtxRef.current.close(); } catch (e) { devWarn("audioctx-close-onstop", e); }
          audioCtxRef.current = null;
        }
        setAudioLevel(0);
        // iter v32 r49j — User M1457: warning audio quasi silenzioso PRIMA
        // di chiamare il backend. Se il peak audio level durante l'intera
        // registrazione è stato < 0.02 (=20/1024, virtualmente nulla),
        // l'audio è silenzioso → utente vedeva "0 parole" senza capire.
        // Su iOS è quasi sempre un problema di permesso microfono revocato
        // o app in background che ha perso l'accesso al mic.
        const peak = maxAudioLevelRef.current;
        if (chunks.length === 0) {
          setIsRecording(false); setRecTime(0);
          if (peak < 0.02) {
            toast.error("Audio silenzioso · Vai su Impostazioni iOS → Pronto → e attiva Microfono", { duration: 7000 });
          }
          return;
        }
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (peak < 0.02 && blob.size < 8000) {
          // Audio molto basso E blob molto piccolo → quasi certamente
          // microfono muto. Evitiamo di sprecare la call Whisper.
          toast.error("Audio silenzioso · Vai su Impostazioni iOS → Pronto → Microfono e verifica che sia attivo. Poi riprova.", { duration: 8000 });
          setIsRecording(false); setRecTime(0);
          return;
        }
        await uploadVoice(blob, peak);
        setIsRecording(false);
        setRecTime(0);
      };
      // iter v33 r24 · User M1500 — Timeslice a 5s cosi' `ondataavailable`
      // fira ogni 5 secondi (invece che solo al stop). Ogni chunk viene
      // subito accumulato in IndexedDB come backup: se app va in background
      // o crasha, la voce non e' persa.
      mr.start(5000);
      setRec(mr);
      setIsRecording(true);
      setIsPaused(false);
      setRecTime(0);
      recTimerRef.current = setInterval(() => setRecTime((t) => t + 1), 1000);
    } catch {
      toast.error("Microfono non accessibile · permetti l'accesso dal browser");
    }
  };

  const togglePauseRecording = () => {
    if (!rec) return;
    if (rec.state === "recording") {
      rec.pause();
      setIsPaused(true);
      if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      setAudioLevel(0);
    } else if (rec.state === "paused") {
      rec.resume();
      setIsPaused(false);
      recTimerRef.current = setInterval(() => setRecTime((t) => t + 1), 1000);
    }
  };

  const stopRecording = () => {
    if (rec && rec.state !== "inactive") rec.stop();
    setIsPaused(false);
  };

  const uploadVoice = async (blob, peakLevel = null) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
      fd.append("file", blob, `chunk.${ext}`);
      const { data } = await api.post(`/sopralluogo/${session.id}/voice`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      // iter v32 r49j — User M1457: feedback chiaro SEMPRE su transcript
      // vuoto, anche se backend non setta `silent: true`. Prima il flow
      // tornava silenziosamente a "Inizia a parlare · 0 parole" senza
      // dire all'utente cos'era successo.
      const transcript = (data.transcript || "").trim();
      if (data.skipped || !transcript) {
        const isShort = data?.skipped && !data?.silent;  // audio < 800 bytes
        const reason = isShort
          ? "Registrazione troppo breve · prova a parlare per almeno 5 secondi"
          : (peakLevel !== null && peakLevel < 0.05)
            ? "Audio troppo basso · avvicinati al microfono e riprova"
            : "Nessuna voce captata · controlla che il microfono dell'iPhone non sia coperto";
        toast.message(reason, { duration: 6000 });
        return;
      }
      await onRefresh();
      toast.success(`+${transcript.split(/\s+/).length} parole salvate`, { duration: 2000 });
      // iter v33 r24 · Upload OK → butto il backup IndexedDB, la voce e' salva.
      clearStoredRecording().catch(() => {});
    } catch (e) {
      // iter v33 r14 · notifyError sopprime Network Error in background iOS
      notifyError(e, "Trascrizione fallita · ritenta tra un attimo", { duration: 6000 });
    } finally {
      setTranscribing(false);
    }
  };

  // ── Phase transitions ───────────────────────────────────────────
  // iter v28 r373 — User M1283: se l'utente preme "Adesso fotografa" mentre
  // sta ancora registrando/in pausa, prima ATTENDIAMO lo stop+upload del
  // chunk audio (= la trascrizione viene salvata) e poi cambiamo fase. In
  // precedenza chiamavamo `stopRecording()` (async) e cambiavamo fase
  // immediatamente, perdendo de facto la nota in corso.
  const goToPhotoPhase = async () => {
    if (isRecording && rec && rec.state !== "inactive") {
      // Promise che si risolve quando il chunk è stato uploadato (mr.onstop
      // chiama uploadVoice → onRefresh → setTranscribing(false))
      await new Promise((resolve) => {
        const prevOnStop = rec.onstop;
        rec.onstop = async (ev) => {
          try { if (prevOnStop) await prevOnStop(ev); } finally { resolve(); }
        };
        try { rec.stop(); } catch { resolve(); }
        setIsPaused(false);
      });
    }
    setPhase("photo");
  };

  const goBackToTalk = () => {
    setPhase("talk");
  };

  // iter v29 r243 — User M1399 — Edit inline trascrizione.
  const startEditTranscript = () => {
    setEditedText(aggregatedTranscript);
    setIsEditingTranscript(true);
  };

  const cancelEditTranscript = () => {
    setIsEditingTranscript(false);
    setEditedText("");
  };

  const saveEditedTranscript = async () => {
    const cleaned = (editedText || "").trim();
    if (!cleaned) {
      toast.error("Il testo non può essere vuoto");
      return;
    }
    setSavingEdit(true);
    try {
      await api.post(`/sopralluogo/${session.id}/transcript-edit`, {
        text: cleaned,
      });
      // Override locale → immediato senza dover refetchare; il backend ha
      // già consolidato rooms in {"Generale": cleaned}, quindi il prossimo
      // refresh sarà coerente.
      setTranscriptOverride(cleaned);
      setIsEditingTranscript(false);
      toast.success("Trascrizione aggiornata");
      // Best-effort refresh per allineare lo stato esteso (room_order, ecc.)
      try { onRefresh?.(); } catch (e) { devWarn("refresh-after-edit", e); }
    } catch (e) {
      notifyError(e);
    } finally {
      setSavingEdit(false);
    }
  };

  // iter v29 r246 — Sistema testo: LLM-cleanup live del transcript.
  // Vedi backend /api/sopralluogo/{sid}/transcript-clean.
  // iter v29 r247 — User M1399 (4a iter): «fallo automatico ad ogni pausa».
  // Aggiunto parametro `silent` per evitare toast invadenti quando il
  // cleanup è triggered dalla pausa (debounced 3s) invece che dal tap.
  const cleanTranscriptViaLLM = async ({ silent = false } = {}) => {
    if (cleaningTranscript) return;
    if (!hasTranscript) return;
    setCleaningTranscript(true);
    try {
      const res = await api.post(`/sopralluogo/${session.id}/transcript-clean`);
      const newText = (res?.data?.transcript || "").trim();
      if (newText) {
        setTranscriptOverride(newText);
        if (!silent) {
          toast.success("Testo sistemato · ho corretto nomi vie e refusi");
        }
        try { onRefresh?.(); } catch (e) { devWarn("refresh-after-clean", e); }
      } else if (!silent) {
        toast.error("Niente da correggere");
      }
    } catch (e) {
      if (!silent) notifyError(e);
      else devWarn("auto-clean-failed", e);
    } finally {
      setCleaningTranscript(false);
    }
  };

  // iter v29 r248 — User M1399 (4a iter, scelta confermata):
  // «Pulizia automatica ogni 30 secondi durante la registrazione».
  // Strategia: setInterval(30s) che si avvia quando inizia il recording e
  // si ferma quando viene messo in pausa o fermato. Ad ogni tick prova a
  // pulire SE c'è nuovo testo (rispetto all'ultima pulizia) e non si sta
  // già pulendo/trascrivendo/editando.
  //
  // Usiamo i ref per leggere lo stato corrente DENTRO l'interval senza
  // dover ricreare il timer ad ogni cambio (= reset 30s ogni chunk vocale).
  const autoCleanIntervalRef = useRef(null);
  const lastAutoCleanedTextRef = useRef("");
  const transcriptLatestRef = useRef("");
  const transcribingLatestRef = useRef(false);
  const cleaningLatestRef = useRef(false);
  const editingLatestRef = useRef(false);

  useEffect(() => { transcriptLatestRef.current = aggregatedTranscript; }, [aggregatedTranscript]);
  useEffect(() => { transcribingLatestRef.current = transcribing; }, [transcribing]);
  useEffect(() => { cleaningLatestRef.current = cleaningTranscript; }, [cleaningTranscript]);
  useEffect(() => { editingLatestRef.current = isEditingTranscript; }, [isEditingTranscript]);

  useEffect(() => {
    // Pulisce eventuali interval precedenti
    if (autoCleanIntervalRef.current) {
      clearInterval(autoCleanIntervalRef.current);
      autoCleanIntervalRef.current = null;
    }
    // Avvia solo durante registrazione ATTIVA (non in pausa, non ferma).
    if (!isRecording || isPaused) return;

    autoCleanIntervalRef.current = setInterval(async () => {
      if (transcribingLatestRef.current) return;
      if (cleaningLatestRef.current) return;
      if (editingLatestRef.current) return;
      const curr = (transcriptLatestRef.current || "").trim();
      if (!curr) return;
      if (curr === lastAutoCleanedTextRef.current) return;
      lastAutoCleanedTextRef.current = curr;
      await cleanTranscriptViaLLM({ silent: true });
    }, 30000);

    return () => {
      if (autoCleanIntervalRef.current) {
        clearInterval(autoCleanIntervalRef.current);
        autoCleanIntervalRef.current = null;
      }
    };
  }, [isRecording, isPaused]);

  // iter v29 r248 — Bug fix: dopo una pulizia (override attivo), le nuove
  // chunk vocali finiscono nel raw transcript. La logica di "override still
  // valid" sopra (durante il render) gestisce questo: se il raw è cresciuto
  // oltre l'override, viene preferito il raw. Niente setState in effect.

  // ── Photo capture (in-page camera component) ────────────────────
  const [cameraOpen, setCameraOpen] = useState(false);
  // iter v32 r48 · User M1462 — Step opzionale "Pianta": permette di
  // caricare la planimetria sia dalla galleria del cellulare sia
  // scattandola al volo (se l'agente ha solo la pianta cartacea sul
  // muro/foglio). L'upload riusa `POST /sopralluogo/{sid}/photo?as=floor_plan`
  // che forza il `photo_kind="floor_plan"` lato backend.
  const [planUploading, setPlanUploading] = useState(false);
  const [planCameraOpen, setPlanCameraOpen] = useState(false);
  const planInputRef = useRef(null);

  // iter v33 r56 · Coda foto locale (IndexedDB): mostriamo un chip
  // "N foto in coda" quando ci sono foto salvate localmente che non sono
  // ancora arrivate al server. Tap sul chip → retry immediato di tutte.
  const [pendingQueueCount, setPendingQueueCount] = useState(0);
  const [retryingQueue, setRetryingQueue] = useState(false);

  // Al mount della RecordingPhase: pulizia record vecchi + retry silenzioso
  // di tutte le foto in coda per questa sessione. Se qualcuna passa, il
  // contatore scende. Se restano, appare il chip in alto.
  useEffect(() => {
    if (!session?.id) return;
    let cancelled = false;
    (async () => {
      // Pulizia una tantum record >7gg (non blocking)
      pruneOld().catch(() => {});
      // Retry silenzioso della coda per questa sessione
      const pending = await listPending(session.id);
      if (cancelled) return;
      setPendingQueueCount(pending.length);
      if (pending.length === 0) return;
      // Non retry piu' di 5 in un colpo per non bombardare la rete al reload
      for (const rec of pending.slice(0, 5)) {
        if (cancelled) return;
        try {
          const fd = new FormData();
          fd.append("file", new File([rec.blob], rec.filename, { type: "image/jpeg" }));
          await api.post(`/sopralluogo/${session.id}/photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          await dequeuePhoto(rec.id);
        } catch {
          // resta in coda, ritenteremo al prossimo mount
          await incrementRetry(rec.id);
        }
      }
      if (cancelled) return;
      const still = await listPending(session.id);
      setPendingQueueCount(still.length);
      if (still.length < pending.length) {
        onRefresh?.();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Retry manuale (tap sul chip): ritenta tutte le foto pending.
  const retryPendingQueue = async () => {
    if (!session?.id || retryingQueue) return;
    setRetryingQueue(true);
    try {
      const pending = await listPending(session.id);
      let okCount = 0;
      let failCount = 0;
      for (const rec of pending) {
        try {
          const fd = new FormData();
          fd.append("file", new File([rec.blob], rec.filename, { type: "image/jpeg" }));
          await api.post(`/sopralluogo/${session.id}/photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          await dequeuePhoto(rec.id);
          okCount++;
        } catch {
          await incrementRetry(rec.id);
          failCount++;
        }
      }
      const still = await listPending(session.id);
      setPendingQueueCount(still.length);
      if (okCount > 0) await onRefresh?.();
      if (okCount === pending.length) {
        toast.success(okCount === 1 ? "Foto sincronizzata" : `${okCount} foto sincronizzate`);
      } else if (okCount > 0) {
        toast.warning(`${okCount}/${pending.length} sincronizzate · ${failCount} in coda`);
      } else {
        toast.error("Rete instabile · le foto restano al sicuro, riprova fra poco");
      }
    } finally {
      setRetryingQueue(false);
    }
  };

  const uploadSinglePhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    // iter v33 r56 · User (iPhone): «foto persa dopo chiusura app». Ora
    // il blob viene salvato PRIMA in IndexedDB. Se l'upload fallisce (iOS
    // background suspend) il blob resta e viene rilanciato al prossimo
    // mount o su richiesta esplicita. Zero perdita di dati.
    const queueId = await enqueuePhoto(session.id, file, file?.name || "photo.jpg");
    const doUpload = async () => {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/sopralluogo/${session.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
    };
    try {
      try {
        await doUpload();
      } catch (firstErr) {
        // Retry silenzioso una volta (dopo 1.5s per far respirare la rete)
        await incrementRetry(queueId);
        await new Promise((r) => setTimeout(r, 1500));
        await doUpload();
      }
      await dequeuePhoto(queueId);
      toast.success("Foto aggiunta");
      await onRefresh();
      // Aggiorna lo stato del banner "N foto in coda"
      const still = await listPending(session.id);
      setPendingQueueCount(still.length);
    } catch (err) {
      // Anche il retry ha fallito: il blob resta in IndexedDB.
      // Toast persistente actionable + aggiornamento contatore coda.
      const still = await listPending(session.id);
      setPendingQueueCount(still.length);
      toast.error("Foto non caricata · rete instabile", {
        description: "L'ho tenuta al sicuro sul telefono. Tocca il chip in alto per ritentare.",
        duration: 8000,
      });
    } finally {
      setUploading(false);
    }
  };

  const onPhotoCapture = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    // iter v28 r380 — Upload sequenziale con progress + tolleranza errori:
    // se una foto fallisce continuiamo con le altre invece di bloccare tutto.
    // iter v28 r381 — Upload PARALLELO a chunk di 3 (User M1291 confermato
    // "potenziale"): per 20 foto passiamo da ~2 min a ~40s su 4G normale.
    // 3 è il sweet spot: oltre, il sopralluogo backend va in coda CPU per
    // la pre-flight quality check; sotto, sprechiamo banda mobile.
    setUploadProgress({ done: 0, total: files.length, failed: 0 });
    let okCount = 0;
    let failCount = 0;
    const CHUNK = 3;
    const uploadOne = async (f, idx) => {
      try {
        // iter v28 r383 — Compressione client-side 2000px / 0.88 (User
        // M1293: «sì, sì compressione, anche mille, sì duemila»). Riduce
        // foto iPhone 12MP da ~3-4 MB a ~600-900 KB senza perdita
        // visibile. La camera in-page già scatta a 2000px (vedi
        // InAppCameraCapture r382), quindi qui agisce solo sui file
        // caricati dalla galleria nativa. Skip silenzioso se < 1 MB.
        const compressed = await compressPhoto(f, {
          maxSizeMB: 2,
          maxWidthOrHeight: 2000,
          initialQuality: 0.88,
        });
        const fd = new FormData();
        fd.append("file", compressed);
        await api.post(`/sopralluogo/${session.id}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        okCount++;
      } catch (uploadErr) {
        failCount++;
        console.warn(`Upload foto ${idx + 1}/${files.length} fallito:`, uploadErr);
      } finally {
        // update progress after each completion (independent of chunk order)
        setUploadProgress({
          done: okCount + failCount,
          total: files.length,
          failed: failCount,
        });
      }
    };
    try {
      for (let i = 0; i < files.length; i += CHUNK) {
        const batch = files.slice(i, i + CHUNK);
        await Promise.all(batch.map((f, j) => uploadOne(f, i + j)));
      }
      if (okCount === files.length) {
        toast.success(okCount === 1 ? "Foto aggiunta" : `${okCount} foto aggiunte`);
      } else if (okCount > 0) {
        toast.warning(`${okCount}/${files.length} foto aggiunte`, {
          description: `${failCount} non caricate. Riprova quelle che mancano.`,
        });
      } else {
        toast.error("Upload foto fallito");
      }
      await onRefresh();
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  // iter v32 r48 · User M1462 — Upload pianta dello step "Pianta".
  // Accetta una singola immagine (galleria iOS o camera in-app) e la
  // marca come `floor_plan` via query param `as=floor_plan` lato backend.
  const onPlanCapture = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const file = files[0];
    setPlanUploading(true);
    try {
      // iter v32 r48 · review M1462 — `compressPhoto` usa
      // browser-image-compression che NON gestisce PDF. Se l'utente sceglie
      // una planimetria PDF dal cellulare, la inviamo "raw" al backend
      // (che la salva senza compressione e la espone come asset PDF).
      const isPdf =
        (file.type || "").includes("pdf") ||
        (file.name || "").toLowerCase().endsWith(".pdf");
      const payload = isPdf
        ? file
        : await compressPhoto(file, {
            maxSizeMB: 4,
            maxWidthOrHeight: 2400,
            initialQuality: 0.90,
          });
      const fd = new FormData();
      fd.append("file", payload);
      await api.post(`/sopralluogo/${session.id}/photo?as=floor_plan`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success("Pianta caricata");
      await onRefresh();
    } catch (uploadErr) {
      notifyError(uploadErr, "Caricamento pianta fallito");
    } finally {
      setPlanUploading(false);
    }
  };

  // Adapter per la camera in-page (InAppCameraCapture) che restituisce
  // direttamente un Blob: lo wrappo in un File così possiamo riusare lo
  // stesso onPlanCapture per i 3 ingressi.
  const handlePlanCameraSnapshot = async (blob) => {
    try {
      const file = new File([blob], "planimetria.jpg", { type: "image/jpeg" });
      await onPlanCapture({ target: { files: [file], value: "" } });
    } finally {
      setPlanCameraOpen(false);
    }
  };

  // ── iter v29 r140 · Auto-detect foto prima del finalize ─────────
  // ── Step intermedio rimosso ──────────────────────────────────────
  // iter v33 r34 · User: «Non capisco perche' subito dentro nel sopralluogo
  // mi fai ancora la possibilita' di scegliere cosa fare nelle foto. Non
  // lo metterei piu' nel sopralluogo questa cosa».
  //
  // iter v33 r35 · Rimosso completamente il codice del PhotoTreatmentModal:
  // state + JSX + funzioni helper + costanti. Non ci sono piu' entry point
  // per farlo apparire. L'agente preme "Genera l'immobile" e va DIRETTAMENTE
  // al finalize; per migliorare le foto usa "Migliora tutte" dal tab Foto.
  const startFinalizeFlow = async () => {
    if (totalTranscriptWords === 0 && totalPhotos === 0) {
      toast.error("Registra almeno una nota o scatta qualche foto prima di generare.");
      return;
    }
    if (isRecording && rec) {
      releaseMicrophoneImmediate();
      try { stopRecording(); } catch (e) { devWarn("stop-before-finalize", e); }
    }
    return finalize();
  };

  // ── Finalize → crea l'immobile ──────────────────────────────────
  const finalize = async () => {
    if (totalTranscriptWords === 0 && totalPhotos === 0) {
      toast.error("Registra almeno una nota o scatta qualche foto prima di generare.");
      return;
    }
    // Se sta ancora registrando: ferma il chunk e attendi che venga salvato.
    if (isRecording && rec) {
      releaseMicrophoneImmediate();
      try { stopRecording(); } catch (e) { devWarn("stop-before-finalize", e); }
    }
    setFinalizing(true);
    try {
      let payload = {};
      const addr = session?.address || "";
      const city = session?.city || "";
      if (addr || city) {
        try {
          const res = await nearbySummary(addr, city);
          if (res?.line || Object.keys(res?.details || {}).length) {
            payload = { nearby_line: res.line, nearby_details: res.details, geo: res.geo };
          }
        } catch (e) { devWarn("nearby-enrich", e); }
      }
      const { data } = await api.post(`/sopralluogo/${session.id}/finalize`, payload);
      onFinalize(data);
    } catch (e) {
      toast.error(apiError(e, "Generazione fallita"));
    } finally {
      setFinalizing(false);
    }
  };

  // ── Discard sopralluogo (con doppia conferma esplicita) ─────────
  // iter v28 r349 — Spostato dall'header al fondo, con label ESPLICITA
  // "Annulla sopralluogo" + descrizione chiara di cosa verrà perso.
  // Niente più trash icon ambiguo in alto.
  const discard = () => {
    toast(
      "Annullare il sopralluogo?",
      {
        description:
          totalPhotos > 0 || hasTranscript
            ? `Perderai ${hasTranscript ? `${totalTranscriptWords} parole registrate` : ""}${hasTranscript && totalPhotos > 0 ? " e " : ""}${totalPhotos > 0 ? `${totalPhotos} foto` : ""}. Operazione non reversibile.`
            : "Stai annullando il sopralluogo (non hai ancora registrato nulla).",
        action: {
          label: "Sì, annulla",
          onClick: async () => {
            try {
              await api.delete(`/sopralluogo/${session.id}`);
              toast.success("Sopralluogo annullato");
              onDiscard();
            } catch (e) {
              toast.error(apiError(e, "Impossibile annullare"));
            }
          },
        },
        cancel: { label: "Continua sopralluogo", onClick: () => {} },
        duration: 12000,
      },
    );
  };

  const mm = String(Math.floor(recTime / 60)).padStart(2, "0");
  const ss = String(recTime % 60).padStart(2, "0");

  return (
    <div
      className="max-w-2xl mx-auto"
      data-testid="sopralluogo-recording"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 7.5rem)" }}
    >
      {/* iter v33 r24 · User M1500 — Recovery banner registrazione persa */}
      {recoveryOffer && (
        <div
          className="mb-4 rounded-lg border border-[#C9B27A] bg-[#FBF3E0] px-4 py-3 flex items-start gap-3"
          data-testid="recording-recovery-banner"
        >
          <Microphone size={20} weight="fill" className="text-[#8A7745] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#1C1C1A]">
              Registrazione recuperata
            </div>
            <div className="text-[12px] text-[#4A5A44] mt-0.5">
              Ho trovato una registrazione di ~{Math.max(1, Math.round((recoveryOffer.durationS || 0) / 60))} min della sessione precedente. La trascrivo?
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={recoverLostRecording}
                className="px-3 py-1 rounded-full bg-[#7C8B6E] text-white text-[11.5px] font-semibold hover:bg-[#6B7A5D] active:scale-95 transition"
                data-testid="recording-recover-btn"
              >
                <Check size={11} weight="bold" className="inline mr-1" /> Trascrivi
              </button>
              <button
                onClick={dismissRecoveryOffer}
                className="px-3 py-1 rounded-full bg-white border border-[#E5E7E0] text-[#4A5A44] text-[11.5px] font-semibold hover:bg-[#F1F4ED] active:scale-95 transition"
                data-testid="recording-recover-dismiss"
              >
                Ignora
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter v33 r56 · Chip "N foto in coda" (IndexedDB): appare quando
          ci sono foto salvate in locale che non sono ancora arrivate al
          server. Tap → retry immediato di tutte. */}
      {pendingQueueCount > 0 && (
        <button
          type="button"
          onClick={retryPendingQueue}
          disabled={retryingQueue}
          className="mb-3 w-full flex items-center justify-between rounded-lg border border-[#C9B27A] bg-[#FBF3E0] px-3 py-2.5 text-left active:scale-[0.98] transition"
          data-testid="photo-queue-chip"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <ImageIcon size={18} weight="fill" className="text-[#8A7745] shrink-0" />
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A]">
                {pendingQueueCount === 1
                  ? "1 foto salvata sul telefono"
                  : `${pendingQueueCount} foto salvate sul telefono`}
              </div>
              <div className="text-[10.5px] text-[#4A5A44]">
                {retryingQueue ? "Sto sincronizzando…" : "Tocca per sincronizzare adesso"}
              </div>
            </div>
          </div>
          {retryingQueue
            ? <Spinner size={16} className="text-[#8A7745] animate-spin shrink-0" />
            : <ArrowRight size={14} weight="bold" className="text-[#8A7745] shrink-0" />}
        </button>
      )}

      {/* Header — senza trash icon (causava dataloss accidentale) */}
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">
          Sopralluogo · in corso
        </div>
        <div className="text-[15px] text-[#1C1C1A] font-medium capitalize">
          {session.property_type} · {session.city || "Città non impostata"}
        </div>
      </div>

      {/* Stepper · sempre visibile, mostra a che punto sei */}
      <PhaseStepper
        phase={phase}
        hasTranscript={hasTranscript}
        photoCount={totalPhotos}
        hasPlan={hasPlan}
      />

      {/* Camera component · foto */}
      <InAppCameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={uploadSinglePhoto}
      />

      {/* iter v32 r48 · User M1462 — Camera component dedicato per la
          pianta. Stesso componente, on-capture-snapshot diverso. */}
      <InAppCameraCapture
        open={planCameraOpen}
        onClose={() => setPlanCameraOpen(false)}
        onCapture={handlePlanCameraSnapshot}
      />

      {/* ═════════════════ STEP 1 · RACCONTA ═════════════════ */}
      {phase === "talk" && (
        <>
          <CheckListPromemoria transcript={aggregatedTranscript} />

          <div className="bg-white border border-[#E8E5DE] rounded-2xl overflow-hidden shadow-[0_4px_18px_-8px_rgba(0,0,0,0.08)]">
            {/* Recording status bar */}
            {/* iter v32 r48 · User M1461 — quando lo stato è "in pausa"
                deve gridare visivamente che si può Riprendere. Prima era
                solo un piccolo "In pausa" nella stessa riga rossa: gli
                utenti che mettevano in pausa subito (es. test) si
                confondevano e non sapevano come riprendere. Adesso:
                - registrazione attiva → rosso con waveform
                - IN PAUSA → ambra con icona pausa grande + testo CTA esplicito
                - off (post-stop) → grigio neutro */}
            <div className={`px-5 py-3.5 transition-colors ${
              isRecording
                ? (isPaused
                    ? "bg-amber-50 border-b border-amber-200"
                    : "bg-red-50 border-b border-red-200")
                : "bg-[#F5F4F0] border-b border-[#EDEAE2]"
            }`}>
              {isRecording ? (
                isPaused ? (
                  <div className="flex items-center justify-between gap-3" data-testid="rec-status-paused">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Pause size={18} weight="fill" className="text-amber-700 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[12px] font-semibold text-amber-900 uppercase tracking-[0.16em]">
                          In pausa
                        </div>
                        <div className="text-[11px] text-amber-800/80 mt-0.5">
                          Tocca <span className="font-semibold">Riprendi</span> per continuare
                        </div>
                      </div>
                    </div>
                    <div className="font-mono text-[15px] font-semibold text-amber-900 tabular-nums">{mm}:{ss}</div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3" data-testid="rec-status-on">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 bg-red-600 rounded-full animate-pulse shrink-0" />
                      <span className="text-[12px] font-semibold text-red-800 uppercase tracking-[0.16em] shrink-0">
                        Sto registrando
                      </span>
                      <div className="flex items-end gap-[3px] h-5 ml-1" aria-hidden data-testid="rec-spectrogram">
                        {[0, 1, 2, 3, 4].map((i) => {
                          const factor = 0.55 + 0.45 * Math.sin(i * 1.7 + recTime * 4);
                          const h = Math.max(0.18, Math.min(1, audioLevel * 1.6 * factor));
                          return (
                            <span
                              key={i}
                              className="w-[3px] rounded-full bg-red-600 transition-all duration-75"
                              style={{ height: `${Math.round(h * 100)}%` }}
                            />
                          );
                        })}
                      </div>
                    </div>
                    <div className="font-mono text-[15px] font-semibold text-red-800 tabular-nums">{mm}:{ss}</div>
                  </div>
                )
              ) : (
                <div className="flex items-center justify-between" data-testid="rec-status-off">
                  <span className="text-[12px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">
                    {hasTranscript ? "In pausa · pronto a continuare" : "Pronto a registrare"}
                  </span>
                  <span className="text-[11px] text-[#9A968B]">{totalTranscriptWords} parole</span>
                </div>
              )}
            </div>

            {/* Mic CTA */}
            <div className="p-5 space-y-3">
              {isRecording ? (
                <>
                  {/* iter v32 r48 · User M1461 — bottone Pausa/Riprendi
                      con doppia identità visiva:
                      · in registrazione → nero scuro, label "Metti in pausa"
                      · in pausa → SAGE pieno + pulse animation + label
                        "▶ Riprendi a parlare" (imperativo chiaro)
                      Prima il bottone era piccolo e la transizione fra i
                      due stati era troppo sottile. Adesso il bottone in
                      pausa "urla" visivamente che basta toccarlo per
                      continuare. */}
                  <button
                    onClick={togglePauseRecording}
                    className={`w-full rounded-xl text-[15px] font-semibold flex items-center justify-center gap-2 transition-all shadow-sm text-white ${
                      isPaused
                        ? "py-6 bg-[#7C8B6E] hover:bg-[#6B7A5D] shadow-[0_0_0_4px_rgba(124,139,110,0.25)] animate-resume-pulse text-[17px]"
                        : "py-5 bg-[#1C1C1A] hover:bg-black"
                    }`}
                    data-testid="voice-pause-btn"
                  >
                    {isPaused ? (
                      <>
                        <Play size={26} weight="fill" />
                        Riprendi a parlare
                      </>
                    ) : (
                      <>
                        <Pause size={20} weight="fill" />
                        Metti in pausa · {mm}:{ss}
                      </>
                    )}
                  </button>
                  {/* iter v28 r373 — User M1283: in pausa l'utente era bloccato
                      (nessun modo di "concludere e vedere il testo"). Aggiunto
                      bottone secondario "Concludi nota · genera testo" che fa
                      STOP totale del MediaRecorder → triggera l'upload del
                      chunk → la trascrizione arriva e diventa visibile nel
                      `<details>` sotto. Solo in stato pausa: non disturba il
                      flusso normale di registrazione continua. */}
                  {isPaused && (
                    <button
                      onClick={stopRecording}
                      disabled={transcribing}
                      className="w-full py-3.5 rounded-xl text-[13px] font-semibold flex items-center justify-center gap-2 transition-colors border border-[#E5E1D5] text-[#5A524A] bg-white hover:bg-[#F5F0E6] disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="voice-stop-and-transcribe-btn"
                    >
                      {transcribing ? (
                        <><Spinner size={16} className="animate-spin" /> Trascrivo… aspetta un attimo</>
                      ) : (
                        <><Stop size={16} weight="fill" /> Oppure concludi e vedi il testo</>
                      )}
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={startRecording}
                  disabled={transcribing}
                  className="w-full py-5 bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white rounded-xl text-[15px] font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
                  data-testid="voice-start-btn"
                >
                  {transcribing ? (
                    <><Spinner size={18} className="animate-spin" /> Trascrivo…</>
                  ) : (
                    <><Microphone size={20} weight="fill" /> {hasTranscript ? "Continua a parlare" : "Inizia a parlare"}</>
                  )}
                </button>
              )}

              {isRecording && !isPaused && (
                <p className="text-[11px] text-[#9A968B] italic text-center px-2 leading-snug">
                  Cammina e parla liberamente · ogni 30 secondi Pronto sistema il testo da sola
                </p>
              )}
              {isRecording && isPaused && (
                <p className="text-[11px] text-amber-700 text-center px-2 leading-snug font-medium">
                  La registrazione è ferma. Quando sei pronto tocca <span className="font-semibold">Riprendi</span>.
                </p>
              )}
            </div>
          </div>

          {/* iter v29 r243 — Trascrizione editabile inline.
              User M1399: «non posso modificare le parole scritte a mano».
              Sempre aperta quando c'è testo (no più <details>) — l'agente
              ha bisogno di vedere subito cosa Pronto ha trascritto per
              correggere errori (nomi propri, vie, numeri civici). */}
          {(aggregatedTranscript || transcribing) && (
            <div
              className="mt-4 bg-white border border-[#EDEAE2] rounded-md px-4 py-3"
              data-testid="live-transcript"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11.5px] font-semibold text-[#1C1C1A]">
                    {transcribing
                      ? "Trascrivo quello che hai detto…"
                      : cleaningTranscript
                      ? "Sistemo nomi e refusi…"
                      : "Quello che hai detto finora"}
                  </span>
                  <span className="text-[10px] text-[#9A968B] uppercase tracking-[0.14em]">
                    {transcribing && !hasTranscript ? "…" : `${totalTranscriptWords} parole`}
                  </span>
                </div>
                {hasTranscript && !isEditingTranscript && !transcribing && (
                  <div className="shrink-0 flex items-center gap-1">
                    <button
                      onClick={cleanTranscriptViaLLM}
                      disabled={cleaningTranscript}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-[#7C8B6E] hover:bg-[#F1F4ED] active:bg-[#E8EFE0] transition-colors disabled:opacity-60"
                      title="Sistema nomi vie, refusi e correzioni vocali"
                      data-testid="clean-transcript-btn"
                    >
                      {cleaningTranscript ? (
                        <><Spinner size={11} className="animate-spin" /> Sistemo…</>
                      ) : (
                        <><CleanWand size={12} weight="duotone" /> Sistema testo</>
                      )}
                    </button>
                    <button
                      onClick={startEditTranscript}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-[#7C8B6E] hover:bg-[#F1F4ED] active:bg-[#E8EFE0] transition-colors"
                      data-testid="edit-transcript-btn"
                    >
                      <PencilSimple size={12} weight="bold" /> Modifica
                    </button>
                  </div>
                )}
              </div>

              {isEditingTranscript ? (
                <>
                  <textarea
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                    rows={Math.min(Math.max(4, editedText.split("\n").length + 1), 12)}
                    className="w-full text-[13px] text-[#1C1C1A] leading-[1.6] bg-[#FAFAF7] border border-[#D5DAC9] rounded-md p-3 focus:outline-none focus:border-[#7C8B6E] focus:bg-white transition-colors resize-y"
                    placeholder="Scrivi o correggi il testo del sopralluogo…"
                    autoFocus
                    data-testid="edit-transcript-textarea"
                  />
                  <p className="mt-2 text-[10.5px] text-[#9A968B] italic leading-snug">
                    Correggi nomi di vie, numeri, refusi · poi salva e continua
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={saveEditedTranscript}
                      disabled={savingEdit}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white rounded-md text-[12.5px] font-semibold transition-colors"
                      data-testid="save-transcript-btn"
                    >
                      {savingEdit ? (
                        <><Spinner size={12} className="animate-spin" /> Salvo…</>
                      ) : (
                        <><FloppyDisk size={13} weight="bold" /> Salva modifiche</>
                      )}
                    </button>
                    <button
                      onClick={cancelEditTranscript}
                      disabled={savingEdit}
                      className="inline-flex items-center gap-1 px-3 py-2 bg-white border border-[#E5E2D8] hover:border-[#D5DAC9] text-[#1C1C1A] rounded-md text-[12.5px] font-medium transition-colors"
                      data-testid="cancel-transcript-btn"
                    >
                      <XIcon size={12} weight="bold" /> Annulla
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-[12.5px] text-[#3F3D38] leading-[1.55] whitespace-pre-wrap">
                  {aggregatedTranscript || (
                    <span className="inline-flex items-center gap-1.5 text-[#9A968B] italic">
                      <Spinner size={12} className="animate-spin" /> Pronto sta trascrivendo la nota vocale…
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* iter v29 r243 — "Adesso fotografa" INLINE (non più sticky).
              User M1399: «esce subito il tasto scatta fotografie che dà
              fastidio». Il floating bar sovrapponeva la trascrizione
              durante la review. Ora è una CTA secondaria dentro il flow
              naturale, visibile solo se l'utente ha già del materiale e
              non sta editando il testo. */}
          {!isEditingTranscript && (isRecording || recTime > 0 || hasTranscript || totalPhotos > 0) && (
            <button
              onClick={goToPhotoPhase}
              disabled={goingToPhoto}
              className="mt-4 w-full bg-[#1C1C1A] hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium py-3.5 rounded-md flex items-center justify-center gap-2 shadow-sm transition-all"
              data-testid="goto-photo-btn"
            >
              {goingToPhoto ? (
                <><Spinner size={16} className="animate-spin" /> Salvo la nota vocale…</>
              ) : (
                <><Camera size={16} weight="fill" /> Adesso fotografa <ArrowRight size={14} weight="bold" /></>
              )}
            </button>
          )}
        </>
      )}

      {/* ═════════════════ STEP 2 · FOTOGRAFA ═════════════════ */}
      {phase === "photo" && (
        <>
          {/* Hint card */}
          <div className="bg-[#FAF8F3] border border-[#E5E2D8] rounded-2xl mb-5 px-4 py-3.5" data-testid="photo-phase-hint">
            <div className="text-[11px] font-semibold text-[#1C1C1A] uppercase tracking-[0.16em] mb-1.5">
              Adesso fotografa
            </div>
            <p className="text-[12.5px] text-[#3F3D38] leading-snug">
              Scatta foto stanza per stanza · soggiorno, cucina, camere, bagno, balcone, esterno · Pronto le ordinerà da sola.
            </p>
          </div>

          {/* Photo gallery */}
          <div className="bg-white border border-[#E8E5DE] rounded-2xl overflow-hidden shadow-[0_4px_18px_-8px_rgba(0,0,0,0.08)]">
            <div className="px-5 py-3.5 bg-[#F5F4F0] border-b border-[#EDEAE2] flex items-center justify-between">
              <span className="text-[12px] uppercase tracking-[0.16em] text-[#9A968B] font-semibold">
                {totalPhotos > 0 ? `${totalPhotos} ${totalPhotos === 1 ? "foto raccolta" : "foto raccolte"}` : "Nessuna foto ancora"}
              </span>
              {hasTranscript && (
                <span className="text-[11px] text-[#7C8B6E] font-semibold inline-flex items-center gap-1">
                  <Check size={11} weight="bold" /> {totalTranscriptWords} parole salvate
                </span>
              )}
            </div>

            {totalPhotos > 0 && (
              <div className="p-4">
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5" data-testid="session-photos">
                  {photos.map((p) => (
                    <div key={p.id} className="aspect-square rounded-md overflow-hidden bg-[#EDEAE2] relative group">
                      <img src={fileUrl(p.original_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      {p.photo_kind === "floor_plan" && (
                        <span className="absolute top-1 left-1 text-[8px] uppercase tracking-[0.14em] font-bold text-white bg-[#7C8B6E] px-1 py-0.5 rounded">Pianta</span>
                      )}
                      {/* iter v32 r52 — User M1463: «devo avere la possibilità con
                          una X di cancellare la foto se non mi piace». Tap su
                          X → conferma rapida → DELETE /photos/{id} → refresh.
                          Tap area generosa (28×28) per mobile. */}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm("Eliminare questa foto dal sopralluogo?")) return;
                          try {
                            await api.delete(`/photos/${p.id}`);
                            await onRefresh?.();
                            toast.success("Foto eliminata");
                          } catch (err) {
                            toast.error(apiError(err) || "Errore durante l'eliminazione");
                          }
                        }}
                        aria-label="Elimina foto"
                        title="Elimina foto"
                        className="absolute top-1 right-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/95 hover:bg-white text-[#C0392B] shadow-md transition-all active:scale-90"
                        data-testid={`delete-photo-${p.id}`}
                      >
                        <XIcon size={13} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Camera CTA */}
            <div className="p-5 pt-3">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onPhotoCapture}
                data-testid="photo-input"
              />
              <button
                onClick={() => setCameraOpen(true)}
                disabled={uploading}
                className="w-full py-5 bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white rounded-xl text-[15px] font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
                data-testid="photo-capture-btn"
              >
                {uploading ? (
                  <><Spinner size={18} className="animate-spin" /> {uploadProgress?.total > 1 ? `Carico foto ${uploadProgress.done}/${uploadProgress.total}…` : "Carico foto…"}</>
                ) : (
                  <><Camera size={20} weight="fill" /> {totalPhotos === 0 ? "Scatta la prima foto" : "Scatta un'altra foto"}</>
                )}
              </button>
              <p className="mt-2 text-[11px] text-[#9A968B] italic text-center leading-snug">
                Pronto le ordina per stanza in automatico
              </p>
            </div>
          </div>

          {/* Back to talk · per chi si è ricordato qualcosa */}
          <button
            onClick={goBackToTalk}
            className="mt-4 w-full py-3 bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] text-[#1C1C1A] rounded-xl text-[13px] font-medium flex items-center justify-center gap-2 transition-colors"
            data-testid="back-to-talk-btn"
          >
            <ArrowLeft size={14} weight="bold" /> Torna a parlare · ho dimenticato qualcosa
          </button>
        </>
      )}

      {/* ═════════════════ STEP 3 · PIANTA (opzionale) ═════════════════
          iter v32 r48 · User M1462 — Step dedicato per caricare la
          planimetria PRIMA del finalize. È opzionale: l'agente può
          saltarlo se non ha la pianta. Se invece l'ha caricata
          automaticamente nello step Fotografa (auto-classify), questo
          step mostra direttamente l'anteprima senza chiedere di nuovo.
          3 azioni: 📐 Carica · 📷 Scatta · Salta. */}
      {phase === "plan" && (
        <>
          <input
            ref={planInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={onPlanCapture}
            data-testid="plan-file-input"
          />

          <div
            className="bg-white border border-[#E8E5DE] rounded-2xl p-6 shadow-[0_4px_18px_-8px_rgba(0,0,0,0.08)]"
            data-testid="plan-step-card"
          >
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#7C8B6E]/12 flex items-center justify-center shrink-0">
                <House size={22} weight="duotone" className="text-[#7C8B6E]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[17px] font-bold text-[#1C1C1A] leading-tight">
                  Hai la planimetria?
                </h3>
                <p className="text-[13px] text-[#5A524A] mt-1 leading-snug">
                  {hasPlan
                    ? "Trovata già nello scatto. Puoi cambiarla o andare avanti."
                    : "Caricala adesso così l'annuncio parte già completo per Idealista e Immobiliare. Oppure salta · la metti dopo."}
                </p>
              </div>
            </div>

            {/* Anteprima pianta caricata */}
            {hasPlan && (
              <div className="mb-5 border border-[#E8E5DE] rounded-xl overflow-hidden bg-[#FAFAF7]">
                <div className="aspect-[4/3] w-full max-h-72 flex items-center justify-center bg-white">
                  <img
                    src={planPhotos[0].url}
                    alt="Planimetria caricata"
                    className="max-w-full max-h-full object-contain"
                    data-testid="plan-preview"
                  />
                </div>
                <div className="px-4 py-2.5 flex items-center justify-between bg-[#F1F4ED] border-t border-[#E8E5DE]">
                  <span className="text-[12px] font-medium text-[#5A6A4D] inline-flex items-center gap-1.5">
                    <Check size={12} weight="bold" /> Planimetria caricata
                  </span>
                  <span className="text-[11px] text-[#9A968B]">
                    {planPhotos.length > 1 ? `${planPhotos.length} piante` : ""}
                  </span>
                </div>
              </div>
            )}

            {/* CTA 3 opzioni */}
            <div className="space-y-2">
              <button
                onClick={() => planInputRef.current?.click()}
                disabled={planUploading}
                className="w-full py-4 bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
                data-testid="plan-upload-gallery-btn"
              >
                {planUploading ? (
                  <><Spinner size={16} className="animate-spin" /> Carico…</>
                ) : (
                  <><ImageIcon size={18} weight="fill" /> {hasPlan ? "Cambia planimetria" : "Carica dalla galleria"}</>
                )}
              </button>

              <button
                onClick={() => setPlanCameraOpen(true)}
                disabled={planUploading}
                className="w-full py-4 bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] disabled:opacity-60 text-[#1C1C1A] rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors"
                data-testid="plan-scan-camera-btn"
              >
                <Camera size={18} weight="bold" /> Scatta foto alla pianta
              </button>

              {!hasPlan && (
                <button
                  onClick={() => {
                    // iter v32 r49g · User M1454 — Prima qui scrollavamo
                    // solo al floating CTA, costringendo l'agente a un
                    // secondo tap. Ora "Salta" lancia DIRETTAMENTE il
                    // flow di finalize/analisi: un tap = annuncio.
                    if (!finalizing) {
                      startFinalizeFlow();
                    }
                  }}
                  disabled={finalizing}
                  className="w-full py-3 text-[#9A968B] hover:text-[#5A524A] disabled:opacity-50 text-[13px] font-medium transition-colors"
                  data-testid="plan-skip-btn"
                  type="button"
                >
                  Non ce l&apos;ho ora · salta e genera l&apos;annuncio →
                </button>
              )}
            </div>

            <p className="mt-4 text-[11px] text-[#9A968B] italic text-center leading-snug">
              JPG, PNG o PDF · una sola pianta per annuncio
            </p>
          </div>

          {/* Back to photo */}
          <button
            onClick={() => setPhase("photo")}
            className="mt-4 w-full py-3 bg-white border border-[#E8E5DE] hover:border-[#7C8B6E] text-[#1C1C1A] rounded-xl text-[13px] font-medium flex items-center justify-center gap-2 transition-colors"
            data-testid="back-to-photo-btn"
          >
            <ArrowLeft size={14} weight="bold" /> Torna a fotografare
          </button>
        </>
      )}

      {/* ═════════ FLOATING BOTTOM CTA · contextual ═════════
          iter v28 r374 — User M1284 (IMG_0994): "Adesso fotografa" si
          sovrapponeva al bottone "Inizia a parlare" della card.
          iter v29 r243 — User M1399 "il tasto scatta fotografie che dà
          fastidio": il floating "Adesso fotografa" in talk phase è stato
          spostato INLINE dentro il contenuto (sotto la trascrizione).
          Qui resta SOLO il CTA del finalize (photo phase) + il link
          "Annulla sopralluogo" (sempre disponibile, discreto). */}
      <div
        className="fixed bottom-0 left-0 right-0 lg:left-60 bg-gradient-to-t from-[#FAFAF7] via-[#FAFAF7]/95 to-transparent pt-6 px-4 z-30 pointer-events-none"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)" }}
      >
        <div className="max-w-2xl mx-auto space-y-2 pointer-events-auto">
          {phase === "photo" && (
            <button
              onClick={() => setPhase("plan")}
              disabled={!hasTranscript && totalPhotos === 0}
              className="w-full bg-[#1C1C1A] hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium py-3.5 rounded-md flex items-center justify-center gap-2 shadow-xl transition-all"
              data-testid="goto-plan-btn"
            >
              Avanti · pianta opzionale <ArrowRight size={14} weight="bold" />
            </button>
          )}

          {phase === "plan" && (
            <button
              onClick={startFinalizeFlow}
              disabled={finalizing || (!hasTranscript && totalPhotos === 0)}
              className="w-full bg-[#1C1C1A] hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium py-3.5 rounded-md flex items-center justify-center gap-2 shadow-xl transition-all"
              data-testid="finalize-btn"
            >
              {finalizing ? (
                <><Spinner size={16} className="animate-spin" /> Analizzo il sopralluogo…</>
              ) : (
                <><Sparkle size={16} weight="fill" /> Genera l&apos;immobile <ArrowRight size={14} weight="bold" /></>
              )}
            </button>
          )}

          {/* Annulla sopralluogo · esplicito, in fondo, con conferma doppia */}
          <button
            onClick={discard}
            className="w-full text-[11.5px] text-[#9A968B] hover:text-red-600 font-medium py-1.5 transition-colors inline-flex items-center justify-center gap-1.5"
            data-testid="sopralluogo-discard-btn"
          >
            <Trash size={11} /> Annulla sopralluogo
          </button>
        </div>
      </div>
    </div>
  );
}

export default RecordingPhase;
